import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import Button from '../../../../../components/button/Button';
import AudioPlayer from '../../../../../components/audioPlayer/AudioPlayer';
import ErrorSection from './errorSection/ErrorSection';
import TroBookContent from './troBookContent/TroBookContent';
import RescoringEditMode from './rescoringEditMode/RescoringEditMode';
import { UNTRANSCRIBABLE_MODAL } from '../../../../../constants/reading/TroConstants';
import './ReadingRecord.scss';
import { getTroMarksClassNames } from '../../../../../utils/reading/marksBuilderUtils';

const ReadingRecord = ({
    listOfSentences,
    bookResult,
    bookInformation,
    readingStats,
    patchBookContentRemarks,
    showRevertModal,
    showUntranscribableModal,
    revertToSoapBoxResult,
    confirmationModalType,
    closeConfirmationPopup,
    audioLookerEvent,
    troBookRescoringEvent,
}) => {
    const [isEditMode, setEditMode] = useState(false);
    const { bookId, vrrResult, isValid, canEdit } = bookResult;
    const { audioSrc, isUntranscribable, startPosition, endPosition, isRescored } = vrrResult;
    const disabledEditMode = !isValid || !canEdit || isEmpty(audioSrc);

    useEffect(() => {
        setEditMode(false);
    }, [bookId]);

    useEffect(() => {
        if (isUntranscribable && !disabledEditMode && !isEditMode) showUntranscribableModal();

        return () => closeConfirmationPopup();
    }, [isUntranscribable, disabledEditMode, isEditMode]);

    const isNotRead = isNil(startPosition) || isNil(endPosition);
    const getMarksClassNames = (marks, wordPosition, isEnd, isItalic) =>
        getTroMarksClassNames({
            marks,
            startPosition,
            endPosition,
            wordPosition,
            isEnd,
            isItalic,
            isNotRead,
        });

    // Props which are using for TRO content visualization and Rescoring edit mode
    const commonProps = {
        listOfSentences,
        setEditMode,
        closeConfirmationPopup,
    };

    return (
        <div className="d-flex justify-content-between tro-reading-transcript-wrapper">
            <section className="tro-content">
                <div className="rescoring-block">
                    <Button
                        dataTestId="edit-mode-btn"
                        isDisabled={
                            disabledEditMode || confirmationModalType === UNTRANSCRIBABLE_MODAL
                        }
                        className="btn btn-secondary rescoring-btn edit-btn"
                        text={
                            <>
                                <i className="icon-v2-edit" />
                                Edit Transcript
                            </>
                        }
                        onClick={() => {
                            troBookRescoringEvent();
                            setEditMode(true);
                        }}
                    />
                    {canEdit && isRescored && (
                        <Button
                            dataTestId="main-revert-btn"
                            className="btn btn-ghost rescoring-btn revert-btn"
                            text="Restore original transcript"
                            onClick={showRevertModal}
                        />
                    )}
                </div>
                <TroBookContent
                    {...commonProps}
                    isNotRead={isNotRead}
                    startPosition={startPosition}
                    mergedEndPosition={endPosition}
                    confirmationModalType={confirmationModalType}
                    getMarksClassNames={getMarksClassNames}
                    onRevert={revertToSoapBoxResult}
                />
            </section>
            <div className="side-block">
                <AudioPlayer
                    audioLookerEvent={audioLookerEvent}
                    audioSrc={audioSrc}
                    classNameWrapper="tro-audio-wrapper"
                />
                <ErrorSection readingStats={readingStats} isUntranscribable={isUntranscribable} />
            </div>
            {isEditMode && (
                <RescoringEditMode
                    {...commonProps}
                    isEditMode={isEditMode}
                    bookId={bookId}
                    bookInformation={bookInformation}
                    vrrResult={vrrResult}
                    patchBookContentRemarks={patchBookContentRemarks}
                    audioLookerEvent={audioLookerEvent}
                />
            )}
        </div>
    );
};

ReadingRecord.defaultProps = {
    listOfSentences: [],
    bookResult: {},
    bookInformation: {},
    readingStats: {},
    patchBookContentRemarks: () => {},
    revertToSoapBoxResult: () => {},
    confirmationModalType: null,
    closeConfirmationPopup: () => {},
    showRevertModal: () => {},
    showUntranscribableModal: () => {},
    audioLookerEvent: () => {},
    troBookRescoringEvent: () => {},
};

ReadingRecord.propTypes = {
    listOfSentences: PropTypes.array,
    showRevertModal: PropTypes.func,
    bookResult: PropTypes.object,
    bookInformation: PropTypes.object,
    readingStats: PropTypes.object,
    patchBookContentRemarks: PropTypes.func,
    revertToSoapBoxResult: PropTypes.func,
    confirmationModalType: PropTypes.string,
    closeConfirmationPopup: PropTypes.func,
    showUntranscribableModal: PropTypes.func,
    audioLookerEvent: PropTypes.func,
    troBookRescoringEvent: PropTypes.func,
};

export default ReadingRecord;
